import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  Lock,
  Mail,
  LogIn,
  ShieldCheck,
  TrendingUp,
  Sparkles,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const formatErr = (detail) => {
  if (!detail) return "Erreur de connexion";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
  return String(detail);
};

/**
 * Where to send the user after login. Programme isolation runs server-side
 * via the user's `assigned_programmes`, so there's no UI choice to make at
 * sign-in — everyone goes straight to the dashboard.
 */
function pickLandingFor(user) {
  return user ? "/" : "/login";
}

const HIGHLIGHTS = [
  {
    icon: TrendingUp,
    title: "Pipeline visuel",
    desc: "Kanban drag & drop, leads HOT prioritaires, programme de conversion 20 jours.",
  },
  {
    icon: Sparkles,
    title: "Coach IA quotidien",
    desc: "Brief commercial généré chaque matin par Claude pour cibler les bons leads.",
  },
  {
    icon: ShieldCheck,
    title: "Sécurité renforcée",
    desc: "Authentification JWT, anti brute-force, données chiffrées en transit.",
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login, loading } = useAuth();
  const { companyName } = useSettings();
  const [email, setEmail] = useState("admin@sogi.sn");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Chargement...
      </div>
    );
  }
  // If already logged in, route to the right landing page based on whether
  // the user still needs to pick a programme.
  if (user) {
    const landing = pickLandingFor(user);
    return <Navigate to={landing} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Veuillez renseigner email et mot de passe");
      return;
    }
    setSubmitting(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      // `login` already calls /me so the AuthContext is fresh — but the
      // returned `data.user` is the slim login payload. We re-read from the
      // refreshed `user` via a direct fetch to /me-like data:
      // the simplest path is to trust /me through the next render. For the
      // immediate redirect we use what we got back + the legacy fields.
      const enriched = data?.user || {};
      // Force the picker only when more than 1 programme is genuinely
      // accessible AND none is currently selected. /auth/me applies the
      // single-programme auto-pick already.
      const target = pickLandingFor({
        ...enriched,
        // login response doesn't carry these — use defensive defaults
        is_admin: enriched.role === "admin",
        allowed_programmes: undefined, // unknown until /me lands; let SelectProgrammePage decide
        current_programme: undefined,
      });
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      {/* Left visual panel — hidden on mobile */}
      <aside className="relative hidden overflow-hidden bg-accent-navy text-white lg:flex lg:flex-col">
        <div className="absolute inset-0 gradient-mesh opacity-60" aria-hidden />
        <div
          aria-hidden
          className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-accent-blue/30 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent-gold/25 blur-3xl"
        />
        <div className="relative flex h-full flex-col justify-between p-12">
          {/* Top: brand */}
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">
                {companyName || "SOGI SARL"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">
                CRM Immobilier
              </div>
            </div>
          </div>

          {/* Mid: pitch */}
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
                Le pilotage commercial
                <br />
                <span className="text-accent-gold">à la hauteur</span> de vos projets.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
                Centralisez vos leads, suivez chaque programme immobilier et accélérez vos closings avec un CRM pensé pour l'immobilier sénégalais.
              </p>
            </div>

            <div className="space-y-4">
              {HIGHLIGHTS.map((h) => {
                const Icon = h.icon;
                return (
                  <div key={h.title} className="flex gap-3">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
                      <Icon className="h-4 w-4 text-accent-gold" />
                    </div>
                    <div>
                      <div className="font-heading text-sm font-semibold">{h.title}</div>
                      <div className="text-xs text-white/60">{h.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom: footer */}
          <div className="text-[11px] text-white/50">
            © {new Date().getFullYear()} {companyName || "SOGI SARL"} · Tous droits réservés
          </div>
        </div>
      </aside>

      {/* Right login form */}
      <main className="relative flex items-center justify-center px-6 py-10 sm:px-10">
        <div className="absolute inset-0 lg:hidden gradient-mesh opacity-30" aria-hidden />
        <div className="relative w-full max-w-[420px] animate-fade-in">
          {/* Logo (mobile) */}
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <div
              data-testid="login-logo-wrapper"
              className="flex h-[100px] w-[220px] items-center justify-center rounded-2xl border border-border/60 bg-white px-5 py-4 shadow-floating"
            >
              <img
                src="/sogi-logo.png"
                alt="SOGI SARL"
                className="h-full w-full object-contain"
                data-testid="login-logo"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              CRM Immobilier · Pilotage commercial
            </p>
          </div>

          {/* Logo (desktop) */}
          <div className="mb-8 hidden lg:flex lg:items-center lg:gap-3">
            <div className="flex h-14 w-[150px] items-center justify-center rounded-xl border border-border/60 bg-white px-3 py-2 shadow-soft">
              <img
                src="/sogi-logo.png"
                alt="SOGI"
                className="h-full w-full object-contain"
                data-testid="login-logo"
              />
            </div>
          </div>

          <div className="mb-6">
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Bienvenue
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Connectez-vous pour accéder à votre tableau de bord.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sogi.sn"
                  className="h-11 pl-10"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <a className="text-[11px] font-medium text-muted-foreground hover:text-foreground" href="#">
                  Mot de passe oublié ?
                </a>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pl-10"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-medium text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300 animate-fade-in"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="h-11 w-full gap-2 bg-accent-navy text-white shadow-soft hover:bg-accent-navy/90 hover:shadow-elevated dark:bg-primary dark:text-primary-foreground"
            >
              <LogIn className="h-4 w-4" />
              {submitting ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <p className="mt-6 text-center text-[11px] text-muted-foreground">
            Accès réservé aux utilisateurs autorisés · {new Date().getFullYear()}
          </p>
        </div>
      </main>
    </div>
  );
}
